import type { CompetitiveView } from '../account';

const share = (p: { count: number; total: number }): number => Math.round((p.count / p.total) * 100);

/** ▁▃▅▇ sparkline of a rival's share across runs — enough to read a direction. */
function Spark({ points }: { points: { count: number; total: number }[] }): JSX.Element {
  const blocks = '▁▂▃▄▅▆▇█';
  return (
    <span className="font-mono text-cyan/70" title={points.map((p) => `${p.count}/${p.total}`).join(' → ')}>
      {points.map((p) => blocks[Math.min(blocks.length - 1, Math.round((share(p) / 100) * (blocks.length - 1)))]).join('')}
    </span>
  );
}

/**
 * Who AI names instead of you, whether that is moving, and — the part that is
 * actually actionable — which pages it cites when you lose.
 *
 * All of this was already stored on every run and never read back across time:
 * a single scan listed its competitors, but nothing compared them run to run,
 * and the sources behind a LOST question were only ever shown one question at a
 * time. Aggregated, they are a target list rather than a complaint.
 */
export default function CompetitivePanel({ data }: { data: CompetitiveView | null }): JSX.Element | null {
  if (!data || data.runs === 0) return null;
  if (data.rivals.length === 0 && data.lossSources.length === 0) return null;

  return (
    <div className="mb-5 border border-warm-dark-2 rounded-xl p-4">
      <h3 className="font-serif text-lg mb-1">Who AI names instead of you</h3>
      <p className="text-sm text-text-secondary mb-3">
        Across {data.runs} {data.runs === 1 ? 'run' : 'runs'} in this range. Share = the portion of that run&apos;s
        questions where the name appeared in the answer.
      </p>

      {data.rivals.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-text-secondary font-mono text-xs text-left">
              <th className="py-1 pr-3">Named</th><th className="pr-3">Latest</th><th className="pr-3">Trend</th><th>Seen since</th>
            </tr></thead>
            <tbody>
              {data.rivals.slice(0, 12).map((r) => {
                const first = r.points[0];
                const last = r.points[r.points.length - 1];
                const move = first && last && r.points.length > 1 ? share(last) - share(first) : null;
                return (
                  <tr key={r.name} className="border-t border-warm-dark-2">
                    <td className="py-1.5 pr-3 text-text-primary">{r.name}</td>
                    <td className="pr-3 font-mono">{r.latestShare}%</td>
                    <td className="pr-3">
                      <Spark points={r.points} />
                      {move !== null && move !== 0 && (
                        <span className={`font-mono text-xs ml-2 ${move > 0 ? 'text-[#E0574A]' : 'text-success'}`}>
                          {move > 0 ? '▲' : '▼'}{Math.abs(move)}
                        </span>
                      )}
                    </td>
                    <td className="font-mono text-xs text-text-secondary">{new Date(r.firstSeen).toLocaleDateString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <p className="font-mono text-xs text-text-secondary/70 mt-1">
            A rival trending <span className="text-[#E0574A]">up</span> is taking answers you want; trending{' '}
            <span className="text-success">down</span> is room opening up.
          </p>
        </div>
      )}

      {data.lossSources.length > 0 && (
        <div className="mt-4 pt-3 border-t border-warm-dark-2">
          <p className="text-sm text-text-primary">The pages that beat you</p>
          <p className="text-sm text-text-secondary mb-2">
            Cited in the answers where you were <span className="text-[#E0574A]">absent</span>, ranked by how many of
            your questions they won. This is your off-site target list, not a generic list of directories, but the
            domains AI actually reads for <em>your</em> questions. Get named on these, or publish something better than
            what they have.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.lossSources.map((s) => (
              <span key={s.host} className="font-mono text-xs border border-[#E0574A]/30 rounded px-2 py-1 text-text-secondary">
                {s.host}<span className="text-[#E0574A]/80"> ×{s.queries}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {data.winSources.length > 0 && (
        <div className="mt-3 pt-3 border-t border-warm-dark-2">
          <p className="text-sm text-text-primary">Your pages carrying you</p>
          <p className="text-sm text-text-secondary mb-2">
            Pages of yours that AI cited on a question where it named you. Make more of these.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.winSources.map((s) => (
              <span key={s.host} className="font-mono text-xs border border-success/30 rounded px-2 py-1 text-text-secondary">
                {s.host}<span className="text-success/80"> ×{s.queries}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Cited in the same answer, and not yours. This list used to be merged
          into the one above, so a brand with name-alikes saw half a dozen
          unrelated companies described as pages carrying it. */}
      {data.coSources.length > 0 && (
        <div className="mt-3 pt-3 border-t border-warm-dark-2">
          <p className="text-sm text-text-primary">Cited in the same answers, not yours</p>
          <p className="text-sm text-text-secondary mb-2">
            Other sites the AI cited on questions you won. They are not carrying you and several may
            just share your name, but they show what else the answer was built from.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {data.coSources.map((s) => (
              <span key={s.host} className="font-mono text-xs border border-warm-dark-2 rounded px-2 py-1 text-text-secondary">
                {s.host}<span className="text-text-secondary/70"> ×{s.queries}</span>
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
